/**
 * Renders recurring Activity statistics and one-tap logging through the deep
 * Completion log interface, without knowing how persistence or sync works.
 */
import type { Period, RecurringActivity } from '@life-rpg/shared';
import React, { useEffect, useState } from 'react';

import type { RecurringActivityRepository } from './activityRepository.js';
import { calculateActivityStats } from './activityStats.js';
import { type CompletionLog, useCompletionLog } from './completions/index.js';

interface ActivityLoggingProps {
  activityRepository: Pick<RecurringActivityRepository, 'list'>;
  completionLog: CompletionLog;
  now?: () => Date;
}

const PERIOD_LABEL: Record<Period, string> = {
  day: 'today',
  week: 'this week',
  month: 'this month',
};

const currentPeriodLabel = (activity: RecurringActivity): string =>
  activity.target.every === 1
    ? PERIOD_LABEL[activity.target.per]
    : `this ${activity.target.every}-${activity.target.per} period`;

export const ActivityLogging = ({
  activityRepository,
  completionLog,
  now = () => new Date(),
}: ActivityLoggingProps): React.JSX.Element => {
  const [activities, setActivities] = useState<RecurringActivity[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const completionState = useCompletionLog(completionLog);

  useEffect(() => {
    let active = true;

    const loadActivities = async (): Promise<void> => {
      try {
        const listedActivities = await activityRepository.list();
        if (!active) return;
        setActivities(listedActivities);
        setProblem(null);
      } catch (error) {
        if (active) setProblem(error instanceof Error ? error.message : 'Could not load logging.');
      }
    };

    void loadActivities();

    const handleOnline = (): void => {
      void loadActivities();
    };
    window.addEventListener('online', handleOnline);

    return () => {
      active = false;
      window.removeEventListener('online', handleOnline);
    };
  }, [activityRepository]);

  const log = async (activity: RecurringActivity, qty: number): Promise<void> => {
    setProblem(null);
    try {
      await completionLog.record(activity.id, qty);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not log completion.');
    }
  };

  const displayedProblem = problem ?? completionState.problem;
  const loading = activities === null || completionState.status === 'loading';

  return (
    <section aria-labelledby="activity-logging-heading">
      <h2 id="activity-logging-heading">Log activity</h2>
      {displayedProblem !== null && <p role="alert">{displayedProblem}</p>}
      {completionState.pendingCount > 0 && (
        <p role="status">
          {completionState.pendingCount}{' '}
          {completionState.pendingCount === 1 ? 'completion is' : 'completions are'} waiting to
          sync.
        </p>
      )}
      {loading && displayedProblem === null && <p>Loading activities…</p>}
      {!loading && activities?.length === 0 && <p>Create a recurring activity to start logging.</p>}
      {!loading && activities !== null && activities.length > 0 && (
        <ul className="activity-list logging-list">
          {activities.map((activity) => {
            const stats = calculateActivityStats(activity, completionState.completions, now());
            const progress = stats.currentPeriodCount;
            const isCheckbox =
              activity.quickAdd.length === 1 &&
              activity.quickAdd[0] === 1 &&
              activity.target.qty === 1;
            const isChecked = progress >= activity.target.qty;

            return (
              <li key={activity.id}>
                <article className="activity-card logging-card">
                  <h3>{activity.name}</h3>
                  <p>
                    {stats.lifetimeCount} {activity.unitLabel} total
                  </p>
                  <p>
                    {progress} of {activity.target.qty} {activity.unitLabel}{' '}
                    {currentPeriodLabel(activity)}
                  </p>
                  <p>28-day completion rate: {Math.round(stats.completionRate28Days * 100)}%</p>
                  <progress
                    max={activity.target.qty}
                    value={Math.min(progress, activity.target.qty)}
                  >
                    {progress} of {activity.target.qty}
                  </progress>
                  {isCheckbox ? (
                    <button
                      aria-checked={isChecked}
                      aria-label={`Log ${activity.name}`}
                      className="logging-checkbox"
                      disabled={isChecked}
                      role="checkbox"
                      type="button"
                      onClick={() => void log(activity, 1)}
                    >
                      {isChecked ? 'Done' : 'Mark done'}
                    </button>
                  ) : (
                    <div className="quick-add-actions">
                      {activity.quickAdd.map((qty, index) => (
                        <button
                          key={`${qty}-${index}`}
                          aria-label={`Log ${qty} ${activity.unitLabel} of ${activity.name}`}
                          type="button"
                          onClick={() => void log(activity, qty)}
                        >
                          +{qty} {activity.unitLabel}
                        </button>
                      ))}
                    </div>
                  )}
                </article>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
